import React, { useEffect, useState, useRef } from 'react';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import Tree from 'react-d3-tree';
import { toPng } from 'html-to-image';
import jsPDF from 'jspdf';
import api from '../utils/api';
import { FiDownload, FiEdit2, FiPlus, FiMinus, FiSearch } from 'react-icons/fi';
import Layout from '../components/Layout/Layout';

export default function Org() {
    const [orgData, setOrgData] = useState(null);
    const [translate, setTranslate] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(0.9);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);
    const [potentialManagers, setPotentialManagers] = useState([]);
    const [newManagerIds, setNewManagerIds] = useState([]);
    const [modalSearch, setModalSearch] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [treeKey, setTreeKey] = useState(0);
    const [searchTargetId, setSearchTargetId] = useState(null);

    const treeContainerRef = useRef(null);
    const hasCenteredSearch = useRef(false);
    const navigate = useNavigate();
    const { user } = useUser();
    const isUserAdmin = user && (user.role_id === 1 || user.role_id === '1');

    useEffect(() => {
        fetchOrgData();
        fetchPotentialManagers();
    }, []);

    // ---------------------------------------------------------
    // 1. DATA FETCHING
    // ---------------------------------------------------------
    const fetchOrgData = async () => {
        try {
            const res = await api.get('/org/get-org-chart');

            if (res.data && res.data.status) {
                const rawData = res.data.data;

                // Color Palette for Teams
                const teamColors = [
                    '#ef4444', // Red
                    '#f97316', // Orange
                    '#f59e0b', // Amber
                    '#84cc16', // Lime
                    '#10b981', // Emerald
                    '#06b6d4', // Cyan
                    '#3b82f6', // Blue
                    '#6366f1', // Indigo
                    '#8b5cf6', // Violet
                    '#d946ef', // Fuchsia
                    '#f43f5e', // Rose
                ];

                // Recursive function to assign colors
                const assignColors = (node, color, isRoot = false) => {
                    if (!node) return;

                    let nodeColor = color;

                    // Root (Admin) gets a specific distinct color (e.g. Slate/Dark Gray)
                    if (isRoot) {
                        nodeColor = '#475569';
                    }

                    // Assign the calculated color to the node
                    node.assignedColor = nodeColor;

                    if (node.children && node.children.length > 0) {
                        node.children.forEach((child, index) => {
                            let childColor = nodeColor;

                            // If currently at Root, assign distinct colors to children (Team Leads)
                            if (isRoot) {
                                childColor = teamColors[index % teamColors.length];
                            }
                            // Else, children inherit the parent's color (Team Color)

                            assignColors(child, childColor, false);
                        });
                    }
                };

                if (rawData) {
                    assignColors(rawData, null, true); // Start processing
                    setOrgData(rawData);
                } else {
                    console.warn("Data structure unexpected");
                }
            }
        } catch (e) {
            console.error("Failed to load org chart", e);
        }
    };

    const fetchPotentialManagers = async () => {
        if (!isUserAdmin) return;
        try {
            // Using employee list endpoint to get flat list
            const res = await api.get('/hrms/employee-list');
            if (res.data && res.data.status) {
                setPotentialManagers(res.data.data);
            }
        } catch (e) {
            console.error("Failed to load employees", e);
        }
    };

    // ---------------------------------------------------------
    // 2. LAYOUT & INTERACTION
    // ---------------------------------------------------------
    useEffect(() => {
        const centerTree = () => {
            if (treeContainerRef.current) {
                const { width } = treeContainerRef.current.getBoundingClientRect();
                // Vertical orientation: Horizontally center (width/2) and start lower (y: 200) to clear top node
                setTranslate({ x: width / 2, y: 200 });
            }
        };

        centerTree();
        window.addEventListener('resize', centerTree);
        return () => window.removeEventListener('resize', centerTree);
    }, [orgData]);

    const handleZoomIn = () => setZoom(prev => Math.min(prev + 0.1, 2));
    const handleZoomOut = () => setZoom(prev => Math.max(prev - 0.1, 0.1));
    const handleFitView = () => {
        if (treeContainerRef.current) {
            const { width, height } = treeContainerRef.current.getBoundingClientRect();
            setTranslate({ x: width / 2, y: 200 });
            setZoom(1);
        }
    };

    // ---------------------------------------------------------
    // 3. EDIT HANDLERS
    // ---------------------------------------------------------
    const openEditModal = (e, nodeDatum) => {
        e.stopPropagation(); // Prevent toggling node
        setSelectedNode(nodeDatum);
        // Reset selection
        setNewManagerIds([]);
        setModalSearch('');
        setIsEditModalOpen(true);
    };

    const toggleManagerSelection = (managerId) => {
        setNewManagerIds(prev => {
            if (prev.includes(managerId)) {
                return prev.filter(id => id !== managerId);
            } else {
                return [...prev, managerId];
            }
        });
    };

    const getOrdinal = (n) => {
        const s = ["th", "st", "nd", "rd"];
        const v = n % 100;
        return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    const handleCenterNode = (nodeDatum) => {
        if (!treeContainerRef.current || !nodeDatum?.__rd3t?.coords) return;
        const { width, height } = treeContainerRef.current.getBoundingClientRect();

        const focusZoom = 1.1;
        const targetX = width / 2 - nodeDatum.__rd3t.coords.x * focusZoom;
        const targetY = height / 4 - nodeDatum.__rd3t.coords.y * focusZoom;

        setTranslate({ x: targetX, y: targetY });
        setZoom(focusZoom);
    };

    const handleSaveManager = async () => {
        if (!selectedNode || newManagerIds.length === 0) return;

        try {
            // Logic: Ensure Enova Admin (1) is the last manager in the chain
            // Filter out ID 1 from selection then append it to ensure it's always last
            const finalChain = newManagerIds.filter(id => String(id) !== '1');
            finalChain.push('1');

            const formData = new FormData();
            formData.append('node_id', selectedNode.id);
            formData.append('target_id', finalChain.join(','));

            const res = await api.post('/org/update-node-parent', formData);
            if (res.data && res.data.status) {
                fetchOrgData();
                setIsEditModalOpen(false);
                setNewManagerIds([]);
                setSelectedNode(null);
            } else {
                alert('Update failed: ' + (res.data?.message || 'Unknown error'));
            }
        } catch (error) {
            console.error(error);
            alert('Failed to update reporting structure.');
        }
    };

    // ---------------------------------------------------------
    // 4. DRAG & DROP HANDLERS
    // ---------------------------------------------------------
    const handleDragStart = (e, nodeDatum) => {
        e.dataTransfer.setData('nodeId', nodeDatum.id);
        e.dataTransfer.effectAllowed = 'move';
    };

    const handleDragOver = (e) => {
        e.preventDefault(); // Necessary to allow dropping
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = async (e, targetNodeDatum) => {
        e.preventDefault();
        const draggedNodeId = e.dataTransfer.getData('nodeId');
        const targetId = targetNodeDatum.id;

        if (!draggedNodeId || draggedNodeId === targetId) return;

        // Confirm
        if (!window.confirm(`Move employee #${draggedNodeId} to report to ${targetNodeDatum.name}?`)) return;

        try {
            const formData = new FormData();
            formData.append('node_id', draggedNodeId);
            formData.append('target_id', targetId);

            const res = await api.post('/org/update-node-parent', formData);
            if (res.data && res.data.status) {
                // Refresh chart
                fetchOrgData();
                // alert('Reporting structure updated!');
            } else {
                alert('Update failed: ' + (res.data?.message || 'Unknown error'));
            }
        } catch (error) {
            console.error(error);
            alert('Failed to update reporting structure.');
        }
    };

    // ---------------------------------------------------------
    // 6. SEARCH HANDLER
    // ---------------------------------------------------------
    const handleSearch = (e) => {
        const query = e.target.value;
        setSearchQuery(query);

        if (!query || query.length < 2) {
            if (query === '' && orgData) {
                fetchOrgData();
                setTreeKey(prev => prev + 1);
                hasCenteredSearch.current = false;
                setSearchTargetId(null);
            }
            return;
        }

        if (!orgData) return;
        hasCenteredSearch.current = false;
        setSearchTargetId(null);

        const data = JSON.parse(JSON.stringify(orgData));
        let firstMatchId = null;

        const searchTree = (node) => {
            if (!node) return false;

            const nameMatch = node.name && node.name.toLowerCase().includes(query.toLowerCase());
            const roleMatch = node.attributes?.designation && node.attributes.designation.toLowerCase().includes(query.toLowerCase());

            let childMatch = false;
            if (node.children && node.children.length > 0) {
                node.children.forEach(child => {
                    if (searchTree(child)) {
                        childMatch = true;
                    }
                });
            }

            if (nameMatch || roleMatch || childMatch) {
                // Ensure ancestors and matched nodes are expanded
                node._collapsed = false;
                if (node.__rd3t) node.__rd3t.collapsed = false;

                if (nameMatch || roleMatch) {
                    node.__highlight = true;
                    if (!firstMatchId) firstMatchId = node.id;
                } else {
                    node.__highlight = false;
                }
                return true;
            }

            node.__highlight = false;
            return false;
        };

        const found = searchTree(data);
        if (found) {
            setSearchTargetId(firstMatchId);
            setOrgData(data);
            setTreeKey(prev => prev + 1);
        }
    };

    // ---------------------------------------------------------
    // 7. NODE RENDERING
    // ---------------------------------------------------------
    const getImageUrl = (imagePath) => {
        // const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL || 'http://localhost/ci4-api/public';
        const IMAGE_BASE_URL = process.env.REACT_APP_IMAGEURL;
        if (!imagePath) return `${IMAGE_BASE_URL}/uploads/default-user.png`;
        return imagePath.startsWith('http') ? imagePath : `${IMAGE_BASE_URL}/${imagePath}`;
    };

    const renderForeignObjectNode = ({ nodeDatum, toggleNode, foreignObjectProps }) => {
        const designation = nodeDatum.attributes?.designation || nodeDatum.attributes?.role || 'Employee';

        // Use the color assigned during data processing (Team Color)
        // Default to pink if not found, though traverse ensures it is set.
        let borderColor = nodeDatum.assignedColor || '#ec4899';

        if (nodeDatum.__highlight) {
            borderColor = '#2563eb';
            // Auto-center on the target node
            if (!hasCenteredSearch.current && searchTargetId && nodeDatum.id === searchTargetId && nodeDatum.__rd3t?.coords) {
                setTimeout(() => {
                    if (!hasCenteredSearch.current) {
                        handleCenterNode(nodeDatum);
                        hasCenteredSearch.current = true;
                    }
                }, 300); // Slightly longer delay for stability during expansion animation
            }
        }

        const isNodeRootOrAdmin = nodeDatum.id === 1 || nodeDatum.id === "1" ||
            designation.toLowerCase() === 'admin' ||
            nodeDatum.name?.toLowerCase().includes('admin');

        const canEdit = isUserAdmin && !isNodeRootOrAdmin;

        return (
            <g>
                <foreignObject width={260} height={300} x="-130" y="-120" style={{ overflow: 'visible' }}>
                    <div
                        className={`flex flex-col items-center group cursor-pointer relative transition-all duration-500 p-2 ${nodeDatum.__highlight ? 'scale-110 z-50' : ''}`}
                        onClick={(e) => {
                            toggleNode();
                            handleCenterNode(nodeDatum);
                        }}
                        draggable={canEdit}
                        onDragStart={canEdit ? (e) => handleDragStart(e, nodeDatum) : undefined}
                        onDragOver={canEdit ? handleDragOver : undefined}
                        onDrop={canEdit ? (e) => handleDrop(e, nodeDatum) : undefined}
                    >
                        {/* Avatar Container */}
                        <div className="relative mb-2 transition-all duration-300">
                            {nodeDatum.__highlight && (
                                <div className="absolute inset-0 bg-blue-400/20 blur-3xl rounded-full scale-110 animate-pulse"></div>
                            )}

                            <div className={`w-20 h-20 rounded-full p-1 shadow-xl transition-all relative z-10 ${nodeDatum.__highlight ? 'ring-4 ring-blue-500 ring-offset-2 animate-float bg-blue-50' : 'bg-white'}`}
                                style={{ background: nodeDatum.__highlight ? undefined : `linear-gradient(135deg, ${borderColor}, #fff)` }}>
                                <img
                                    src={getImageUrl(nodeDatum.attributes?.image || nodeDatum.image)}
                                    alt="profile"
                                    className="w-full h-full rounded-full object-cover border-[3px] border-white shadow-md"
                                    onError={(e) => { e.target.onerror = null; e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(nodeDatum.name || '?')}&background=random&color=fff`; }}
                                />
                            </div>

                            <div className="absolute bottom-0 right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white shadow-md z-20"></div>

                            {/* Edit Icon Badge - Hidden for Admin Node, shown only if User is Admin */}
                            {canEdit && (
                                <div
                                    onClick={(e) => { e.stopPropagation(); openEditModal(e, nodeDatum); }}
                                    className="absolute -top-1 -right-1 w-7 h-7 bg-white rounded-full border border-gray-100 flex items-center justify-center shadow-lg hover:bg-purple-600 hover:text-white transition-all z-30 group/edit"
                                >
                                    <FiEdit2 size={12} className="text-gray-500 group-hover/edit:text-white" />
                                </div>
                            )}

                            {nodeDatum.children && nodeDatum.children.length > 0 && (
                                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-5 bg-white rounded-full border border-gray-100 flex items-center justify-center shadow-md z-30 group/icon relative">
                                    {nodeDatum.__rd3t.collapsed ? (
                                        <FiPlus size={10} className="text-purple-600" />
                                    ) : (
                                        <FiMinus size={10} className="text-purple-600" />
                                    )}
                                    {/* Tooltip */}
                                    <div className="absolute left-full top-1/2 -translate-y-1/2 ml-2 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow-lg opacity-0 group-hover/icon:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-50">
                                        {nodeDatum.__rd3t.collapsed ? 'Expand' : 'Collapse'}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Designation */}
                        <div className={`px-3 py-1.5 rounded-xl text-white text-[8px] font-bold uppercase tracking-wide shadow-lg mb-1.5 transition-all relative z-10 max-w-[180px] text-center leading-tight whitespace-normal ${nodeDatum.__highlight ? 'bg-blue-600 ring-2 ring-white shadow-blue-200' : ''}`}
                            style={{ backgroundColor: nodeDatum.__highlight ? undefined : borderColor }}>
                            {designation}
                        </div>

                        {/* Name Card */}
                        <div className={`px-4 py-2 rounded-xl shadow-xl border min-w-[140px] text-center transition-all relative z-10 ${nodeDatum.__highlight ? 'bg-white border-blue-400 ring-2 ring-blue-50' : 'bg-white border-gray-50'}`}>
                            <h3 className={`text-xs font-black truncate leading-tight ${nodeDatum.__highlight ? 'text-blue-900' : 'text-gray-800'}`}>
                                {nodeDatum.name}
                            </h3>
                            <div className="text-[8px] text-gray-400 mt-0.5 font-bold uppercase tracking-tight">
                                {nodeDatum.attributes?.designation || nodeDatum.attributes?.role || 'Staff'}
                            </div>
                        </div>
                    </div>
                </foreignObject>
            </g>
        );
    };


    // ---------------------------------------------------------
    // 7. DOWNLOAD HANDLERS
    // ---------------------------------------------------------
    const downloadChart = async (format) => {
        if (!treeContainerRef.current) return;
        try {
            const dataUrl = await toPng(treeContainerRef.current, { backgroundColor: '#f9fafb', cacheBust: true });
            if (format === 'png') {
                const link = document.createElement('a');
                link.download = 'org-chart.png';
                link.href = dataUrl;
                link.click();
            } else if (format === 'pdf') {
                const pdf = new jsPDF('l', 'mm', 'a3');
                const imgProps = pdf.getImageProperties(dataUrl);
                const pdfWidth = pdf.internal.pageSize.getWidth();
                const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
                pdf.addImage(dataUrl, 'PNG', 0, 0, pdfWidth, pdfHeight);
                pdf.save('org-chart.pdf');
            }
        } catch (e) {
            console.error("Download failed", e);
            alert('Could not download chart.');
        }
    };

    return (
        <Layout noSidebar={true}>
            <style>{`
                .rd3t-link {
                    stroke: #64748b !important; 
                    stroke-width: 2px !important;
                    fill: none !important;
                    transition: all 0.3s ease;
                }
                .rd3t-link:hover {
                    stroke: #8b5cf6 !important; 
                    stroke-width: 4px !important;
                    filter: drop-shadow(0 0 5px rgba(139, 92, 246, 0.3));
                }
                @keyframes float {
                    0% { transform: translateY(0px); }
                    50% { transform: translateY(-5px); }
                    100% { transform: translateY(0px); }
                }
                .animate-float {
                    animation: float 3s ease-in-out infinite;
                }
            `}</style>

            <div style={{ height: 'calc(100vh - 80px)', position: 'relative', overflow: 'hidden', background: '#f9fafb' }} ref={treeContainerRef}>
                {/* Header / Controls */}
                <div className="absolute top-4 left-4 z-10 bg-white/90 backdrop-blur rounded-lg shadow-sm p-4 border border-gray-100 flex flex-col gap-2">
                    <div>
                        <h1 className="text-xl font-bold text-gray-800">Organization Chart</h1>
                        <p className="text-sm text-gray-500">Employee Hierarchy</p>
                    </div>

                    {/* Search Bar */}
                    <div className="mt-1">
                        <input
                            type="text"
                            placeholder="Search employee..."
                            className="w-full text-xs border border-gray-300 rounded px-2 py-1 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                            value={searchQuery}
                            onChange={handleSearch}
                        />
                    </div>

                    <div className="flex gap-2 mt-2">
                        <button onClick={handleZoomIn} className="p-2 bg-gray-100 rounded hover:bg-gray-200 w-8 h-8 flex items-center justify-center font-bold" title="Zoom In">+</button>
                        <button onClick={handleZoomOut} className="p-2 bg-gray-100 rounded hover:bg-gray-200 w-8 h-8 flex items-center justify-center font-bold" title="Zoom Out">-</button>
                        <button onClick={handleFitView} className="p-2 bg-gray-100 rounded hover:bg-gray-200 px-3 text-xs font-bold" title="Reset View">Fit</button>
                    </div>
                </div>




                {orgData ? (
                    <Tree
                        key={treeKey}
                        data={orgData}
                        translate={translate}
                        orientation="vertical"
                        separation={{ siblings: 1.1, nonSiblings: 1.3 }}
                        nodeSize={{ x: 220, y: 300 }}
                        zoomable={true}
                        draggable={true}
                        zoom={zoom}
                        scaleExtent={{ min: 0.1, max: 2 }}
                        rootNodeClassName="node__root"
                        branchNodeClassName="node__branch"
                        leafNodeClassName="node__leaf"
                        renderCustomNodeElement={(rd3tProps) =>
                            renderForeignObjectNode({ ...rd3tProps })
                        }
                        collapsible={true}
                        initialDepth={1}
                        transitionDuration={800}
                    />
                ) : (
                    <div className="flex items-center justify-center h-full text-gray-500">
                        <div className="animate-pulse flex flex-col items-center">
                            <div className="h-4 w-32 bg-gray-200 rounded mb-4"></div>
                            <div className="text-sm">Loading Hierarchy...</div>
                        </div>
                    </div>
                )}
            </div>

            {/* EDIT MODAL */}
            {isEditModalOpen && selectedNode && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg flex flex-col max-h-[90vh]">
                        <div className="p-6 border-b">
                            <h3 className="text-lg font-bold text-gray-800">
                                Move {selectedNode.name}
                            </h3>
                            <p className="text-sm text-gray-500">Select one or more managers (ordered by selection)</p>
                        </div>

                        <div className="p-4 border-b bg-gray-50">
                            <input
                                type="text"
                                placeholder="Filter managers..."
                                className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-purple-500 focus:outline-none"
                                value={modalSearch}
                                onChange={(e) => setModalSearch(e.target.value)}
                            />
                        </div>

                        <div className="flex-1 overflow-y-auto p-4 space-y-2">
                            {potentialManagers
                                .filter(u => u.user_id !== selectedNode.id) // Filter self
                                .filter(u => u.account_status == 1) // Active only
                                .filter(u => u.attendance_flag != 0) // Not flagged
                                .filter(u => `${u.first_name} ${u.last_name} ${u.designation_name}`.toLowerCase().includes(modalSearch.toLowerCase()))
                                .map(manager => {
                                    const isSelected = newManagerIds.includes(manager.user_id);
                                    const orderIndex = newManagerIds.indexOf(manager.user_id);
                                    return (
                                        <div
                                            key={manager.user_id}
                                            onClick={() => toggleManagerSelection(manager.user_id)}
                                            className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-all ${isSelected
                                                ? 'bg-purple-50 border-purple-500 ring-1 ring-purple-500'
                                                : 'bg-white border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                                }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-gray-200 overflow-hidden flex-shrink-0">
                                                    {/* Simple Avatar Placeholder or Img */}
                                                    <div className="w-full h-full flex items-center justify-center text-xs font-bold text-gray-500">
                                                        {manager.first_name[0]}{manager.last_name[0]}
                                                    </div>
                                                </div>
                                                <div>
                                                    <div className="text-sm font-semibold text-gray-800">{manager.first_name} {manager.last_name}</div>
                                                    <div className="text-xs text-gray-500">{manager.designation_name || manager.role}</div>
                                                </div>
                                            </div>

                                            {isSelected && (
                                                <span className="bg-purple-600 text-white text-xs font-bold px-2 py-1 rounded-full shadow-sm">
                                                    {getOrdinal(orderIndex + 1)}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })
                            }
                            {potentialManagers.length === 0 && <div className="text-center text-gray-400 py-4">No managers found</div>}
                        </div>

                        <div className="p-6 border-t flex justify-end gap-3 bg-white rounded-b-xl">
                            <button
                                onClick={() => setIsEditModalOpen(false)}
                                className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-lg font-medium transition-colors"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveManager}
                                disabled={newManagerIds.length === 0}
                                className={`px-4 py-2 rounded-lg text-white font-medium transition-colors ${newManagerIds.length === 0 ? 'bg-purple-300 cursor-not-allowed' : 'bg-purple-600 hover:bg-purple-700 shadow-md'
                                    }`}
                            >
                                Save Changes {newManagerIds.length > 0 && `(${newManagerIds.length})`}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </Layout >
    );
}
